import React, { useReducer, useEffect, useContext } from "react";
import api from "../../BrandOnBoarding/api";
import { AuthContext } from "../../context/auth-context";
import { resolveDefaultKeywordOptions } from "../../utils/keywordOptions";

const sanitizeKey = (key) => {
  return key.replace(/[.#$\/\[\]]/g, "_");
};

const sanitizeObject = (obj) => {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(sanitizeObject);
  }
  return Object.keys(obj).reduce((result, key) => {
    const sanitizedKey = sanitizeKey(key);
    result[sanitizedKey] = sanitizeObject(obj[key]);
    return result;
  }, {});
};

function hasData(obj, type) {
  const baseExpectedTypes = {
    name: "string",
    llm: "string",
    type: "string",
    competitors: "object",
    brand_scores: "object",
    category_dimensions: "object",
  };

  let expectedTypes = { ...baseExpectedTypes };

  if (type === "brand") {
    expectedTypes.core_attributes = "object";
    // expectedTypes.top_products = "object";
    expectedTypes.brand_description = "string";
    expectedTypes.brand_snippets = "object";
    expectedTypes.brand_themes = "object";
    expectedTypes.entity = "string";
    expectedTypes.positive_negative_attributes = "object";
    // expectedTypes.sources=  "object";
  } else if (type === "product") {
    expectedTypes.taxonomies = "object";
    expectedTypes.brand_description = "string";
    expectedTypes.brand_snippets = "object";
    expectedTypes.brand_themes = "object";
    expectedTypes.entity = "string";
    expectedTypes.positive_negative_attributes = "object";
    // expectedTypes.sources=  "object";
  } else if (type === "category") {
    expectedTypes.taxonomies = "object";
  }

  for (const key in expectedTypes) {
    if (expectedTypes.hasOwnProperty(key)) {
      const expectedType = expectedTypes[key];
      const actualValue = obj[key];

      // Check if the property exists in the object
      if (!(key in obj)) {
        return false;
      }

      const actualType = Array.isArray(actualValue)
        ? "object"
        : typeof actualValue;

      if (actualType !== expectedType) {
        return false;
      }

      if (expectedType === "object" && Array.isArray(actualValue)) {
        if (
          actualValue.length === 0 &&
          key !== "category_dimensions" &&
          key !== "core_attributes" &&
          key !== "positive_negative_attributes" &&
          key !== "brand_themes"
        ) {
          return false;
        }
        if (key === "brand_scores") {
          if (
            type === "category"
              ? actualValue.length === 0
              : actualValue.length !== 4 ||
              actualValue.some(
                (item) =>
                  item === null ||
                  item === undefined ||
                  !item.hasOwnProperty("total") ||
                  item.hasOwnProperty("error")
              )
          ) {
            return false;
          }
        }
      } else if (expectedType === "object" && !Array.isArray(actualValue)) {
        // Check if the object is empty for specific keys
        if (
          (key === "category_dimensions" ||
            key === "core_attributes" ||
            key === "positive_negative_attributes" ||
            key === "brand_themes") &&
          Object.keys(actualValue).length === 0
        ) {
          return false;
        }
      } else if (expectedType === "string" && actualValue.trim() === "") {
        return false;
      }
    }
  }
  return true;
}

const uniqueArray = (array) => {
  return array.filter(
    (obj, index, self) => index === self.findIndex((o) => o?.name === obj?.name)
  );
};

const updateCount = (totalCount, completedTaskCount) => {
  return {
    totalCount,
    completedTaskCount,
  };
};


const DEFAULT_LANGUAGE_LIST = [{ name: "English", value: "en" }, { name: "Italian", value: "it" }];

function getCachedLanguageList() {
  try {
    const cached = localStorage.getItem("languageList");
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch (_) {}
  return DEFAULT_LANGUAGE_LIST;
}

const INITIAL_STATE = {
  name: "",
  language: window.selectedLanguage || "en",
  languageList: getCachedLanguageList(),
  entity: "",
  category: "",
  categoryType: "",
  market: "",
  transactionStatus: "",
  sourceValidationStatus: "",
  llm: "gpt-5.6",
  type: "brand",
  loading: false,
  competitors: [],
  brand_scores: [],
  category_dimensions: {},
  non_branded_questions_results: {
    "open-ended": { meta: [], baseResponses: [] },
    "fact-based": { meta: [], baseResponses: [] },
  },
  core_attributes: {},
  sources: [],
  brand_description: "",
  brand_snippets: [],
  brand_themes: [],
  positive_negative_attributes: [],
  top_products: [],
  taxonomies: [],
  is_launched: false,
  abort_launch: false,
  is_resetFormState: false,
  location: "United States",
  occupation: "",
  attributes: [],
  category_taxonomies: [],
  product_prefs: [],
  pre_category_dimensions: [],
  rank_scores: [],
  status: {},
  customTemplateID: null,
  customIndexScoreType: null,
  rankCompetitiveSet: [],
  rankMethodology: "by_category",
  metricSettingObject: {},
  keywords: ["Top", "Best", "Most Recommended"],
  isBatchMode: false,
  isNonBrandedQuestions: false,
  isFactBasedQuestions: false,
  isShared: false,
  sharedAt: null,
  ownerUserId: null,
};

export const DiscoveryContext = React.createContext({
  location: "",
  language: "en",
  languageList: [{ name: "English", value: "en" }, { name: "Italian", value: "it" }],
  occupation: "",
  attributes: [],
  product_prefs: [],
  state: {},
  category: "",
  categoryType: "",
  market: "",
  llm: "",
  type: "",
  loading: false,
  status: {},
  updateId: () => { },
  updateLoading: () => { },
  brand_scores: [],
  category_taxonomies: [],
  updateCategoryTaxonomies: () => { },
  updateBrandScores: () => { },
  sources: [],
  updateSources: () => { },
  core_attributes: [],
  updateCoreAttributes: () => { },
  competitors: [],
  updatedCompetitors: () => { },
  category_dimensions: {},
  updateCategoryDimensions: () => { },
  non_branded_questions_results: {
    "open-ended": { meta: [], baseResponses: [] },
    "fact-based": { meta: [], baseResponses: [] },
  },
  updateNonBrandedQuestionsResults: () => { },
  brand_description: "",
  updateBrandDescription: () => { },
  brand_snippets: [],
  updateBrandSnippets: () => { },
  positive_negative_attributes: [],
  updatePositiveNegativeAttributes: () => { },
  top_products: [],
  updatedTopProducts: () => { },
  name: "",
  entity: "",
  pre_category_dimensions: [],
  rank_scores: [],
  updateEntity: () => { },
  updateName: () => { },
  updateType: () => { },
  is_launched: false,
  is_resetFormState: false,
  updatedLaunched: () => { },
  updateEntireState: () => { },
  resetState: () => { },
  resetForRelaunch: () => { },
  resetFormState: () => {},
  refreshState: () => { },
  brand_themes: [],
  updatedBrandThemes: () => { },
  updateCategory: () => { },
  updateCategoryType: () => { },
  updateMarket: () => { },
  updateLLM: () => { },
  taxonomies: {},
  updatedTaxonomies: () => { },
  abort_launch: false,
  updateAbortLaunch: () => { },
  updateLocation: () => { },
  updateOccupation: () => { },
  updateAttribute: () => { },
  updateProductPrefs: () => { },
  updatePreCategoryDimensions: () => { },
  updateRankScores: () => { },
  replaceBrandScores: () => { },
  updateLanguage: () => { },
  updateTransactionId: () => { },
  updateStatus: () => { },
  updateTransactionStatus: () => { },
  updateTransactionHistory: () => { },
  updateSourceValidationStatus:() => { },
  updateShareInfo: () => { }
});

function discoveryReducer(state = INITIAL_STATE, action) {
  if (action.type === "UPDATE_BRAND_SCORE") {
    const { data, index,sentimentSources,isValidated } = action.payload;
    if (!state.entity) return state;
    const updatedBrandScores = [...state.brand_scores];
    const rankingScores =
      state.rank_scores.data && state.rank_scores.data.length > 0
        ? [...state.rank_scores.data]
        : [];
    const rankDataForBrandProduct = rankingScores[index];
    if (rankDataForBrandProduct && data && data?.name) {
      updatedBrandScores[index] = {
        name: data.name,
        rank: rankDataForBrandProduct.score,
        favorability: data.favorability,
        total: +rankDataForBrandProduct.score + +data.favorability,
        extras: {
          ...data.extras,
          ...state.rank_scores.extras,
          rank: [rankDataForBrandProduct],
        sentimentSources:sentimentSources,
        isValidated
        },
      };
    } else {
      updatedBrandScores[index] = data;
    }
    return {
      ...state,
      brand_scores: updatedBrandScores,
    };
  }


if (action.type === "UPDATE_SOURCES") {
  const { sources, showOldSourcesRecord, type } = action.payload;

  if (showOldSourcesRecord) {
    const mergedSources = sources.reduce(
      (acc, obj) => ({ ...acc, ...obj }),
      {}
    );
    return {
      ...state,
      sources: {
        ...state.sources,
        [type]: {
          ...(state.sources?.[type] || {}),
          ...mergedSources,
        },
      },
    };
  } else {
    const payloadArray = Array.isArray(sources) ? sources : [sources];
    return {
      ...state,
      sources: {
        ...state.sources,
        [type]: [
          ...(Array.isArray(state.sources?.[type]) ? state.sources[type] : []),
          ...payloadArray,
        ],
      },
    };
  }
}



  if (action.type === "UPDATE_CORE_ATTRIBUTES") {
    return {
      ...state,
      core_attributes: action.payload,
    };
  }
  if (action.type === "UPDATE_COMPETITORS") {
    return {
      ...state,
      competitors: action.payload,
    };
  }
  if (action.type === "UPDATE_CATEGORY_DIMENSIONS") {
    return {
      ...state,
      category_dimensions: action.payload,
    };
  }
  if (action.type === "UPDATE_NON_BRANDED_QUESTIONS_RESULTS") {
    const { questionsType = "open-ended", meta, baseResponses } = action.payload;
    return {
      ...state,
      non_branded_questions_results: {
        ...state.non_branded_questions_results,
        [questionsType]: { meta, baseResponses },
      },
    };
  }
  if (action.type === "UPDATE_BRAND_DESCRIPTION") {
    return {
      ...state,
      brand_description: action.payload,
    };
  }
  if (action.type === "UPDATE_BRAND_SNIPPETS") {
    let updated_brand_snippets = { ...state.brand_snippets };
    let { res } = action.payload;
    updated_brand_snippets = res;
    return {
      ...state,
      brand_snippets: updated_brand_snippets,
    };
  }
  // if (action.type === "UPDATE_POSITVE_NEGATIVE_ATTRIBUTES") {
  //   return {
  //     ...state,
  //     positive_negative_attributes: action.payload,
  //   };
  // }
  if (action.type === "UPDATE_POSITVE_NEGATIVE_ATTRIBUTES") {
  return {
    ...state,
    positive_negative_attributes: [
      ...(state.positive_negative_attributes || []), 
      action.payload, 
    ],
  };
}
  if (action.type === "UPDATE_TOP_PRODUCTS") {
    return {
      ...state,
      top_products: action.payload,
    };
  }
  if (action.type === "UPDATE_ENTITY") {
    return {
      // ...INITIAL_STATE,
      ...state,
      entity: action.payload,
    };
  }
  if (action.type === "UPDATE_LAUNCHED") {
    return {
      ...state,
      is_launched: action.payload,
    };
  }
  if (action.type === "UPDATE_STATE") {
    return {
      ...action.payload,
    };
  }
  if (action.type === "RESET_STATE") {
    return {
      ...INITIAL_STATE,
      type: state.type,
      transactionHistory: state.transactionHistory,
      language: state.language,
      languageList: state.languageList,
    };
  }
  if (action.type === "RESET_FOR_RELAUNCH") {
    return {
      ...INITIAL_STATE,
      // preserve form input values
      entity: state.entity,
      category: state.category,
      categoryType: state.categoryType,
      market: state.market,
      llm: state.llm,
      competitors: state.competitors.filter(c => c?.name?.toLowerCase() !== state.entity?.toLowerCase()),
      name: state.name,
      // preserve meta / config
      type: state.type,
      language: state.language,
      languageList: state.languageList,
      transactionHistory: state.transactionHistory,
      keywords: state.keywords,
      customTemplateID: state.customTemplateID,
      customIndexScoreType: state.customIndexScoreType,
      rankCompetitiveSet: state.rankCompetitiveSet,
      rankMethodology: state.rankMethodology,
      location: state.location,
      occupation: state.occupation,
      attributes: state.attributes,
      product_prefs: state.product_prefs,
      pre_category_dimensions: state.pre_category_dimensions,
      category_taxonomies: state.category_taxonomies,
    };
  }
  if(action.type === "RESET_FORMSTATE"){
    return {
      ...state,
      is_resetFormState : action.payload,
    }
  }
  if (action.type === "REFRESH_STATE") {
    return {
      ...INITIAL_STATE,
      type: state.type,
      transactionHistory: state.transactionHistory,
      language: state.language,
      languageList: state.languageList,
      is_launched: true,
      state: { ...INITIAL_STATE, languageList: state.languageList }
    };
  }
  if (action.type === "UPDATE_BRAND_THEMES") {
    let updated_brand_themes = { ...state.brand_themes };
    let { res } = action.payload;
    updated_brand_themes = res;
    return {
      ...state,
      brand_themes: updated_brand_themes,
    };
  }
  if (action.type === "UPDATE_NAME") {
    return {
      ...state,
      name: action.payload,
    };
  }
  if (action.type === "UPDATE_CATEGORY") {
    return {
      ...state,
      category: action.payload,
    };
  }
  if (action.type === "UPDATE_CATEGORY_TYPE") {
    return {
      ...state,
      categoryType: action.payload,
    };
  }
  if (action.type === "UPDATE_MARKET") {
    return {
      ...state,
      market: action.payload,
    };
  }
  if (action.type === "UPDATE_LLM") {
    return {
      ...state,
      llm: action.payload,
    };
  }
  if (action.type === "UPDATE_TYPE") {
    return {
      ...state,
      type: action.payload,
    };
  }
  if (action.type === "UPDATE_TAXONOMY") {
    
    return {
      ...state,
      taxonomies: action.payload,
    };
  }
  if (action.type === "UPDATE_ID") {
    return {
      ...state,
      id: action.payload,
    };
  }
  if (action.type === "UPDATE_TRANSACTIONID") {
    return {
      ...state,
      transactionId: action.payload,
    };
  }
    if (action.type === "UPDATE_STATUS") {
   let { totalCount, completedTaskCount } = action.payload;
      totalCount = totalCount ?? state.status?.totalCount ?? 0;
     completedTaskCount = completedTaskCount ?? state.status?.completedTaskCount ?? 0;
    return {
      ...state,
      status: updateCount(totalCount, completedTaskCount),
    };
  }
  if (action.type === "UPDATE_TRANSACTIONSTATUS") {
    return {
      ...state,
      transactionStatus: action.payload,
    };
  }

  if (action.type === "UPDATE_SOURCEVALIDATIONSTATUS") {
    return {
      ...state,
      sourceValidationStatus: action.payload,
    };
  }

  if (action.type === "UPDATE_SHARE_INFO") {
    return {
      ...state,
      isShared: !!action.payload?.is_shared,
      sharedAt: action.payload?.shared_at ?? null,
      ownerUserId: action.payload?.owner_user_id ?? null,
    };
  }

if (action.type === "UPDATE_TRANSACTIONHISTORY") {
    if (action.payload === null || action.payload.clear) {
      return { ...state, transactionHistory: [] };
    }
    if (action.payload.replace) {
      return { ...state, transactionHistory: action.payload.data || [] };
    }
return {
  ...state,
  transactionHistory: action.payload || [],  
};
  }
  if (action.type === "UPDATE_LANGUAGE") {
    window.selectedLanguage = action.payload;
    return {
      ...state,
      language: action.payload,
    };
  }
  if (action.type === "UPDATE_LANGUAGE_LIST") {
    return {
      ...state,
      languageList: action.payload,
    };
  }
  if (action.type === "ABORT_LAUNCH") {
    return {
      ...state,
      abort_launch: action.payload,
    };
  }
  if (action.type === "UPDATE_LOCATION") {
    return {
      ...state,
      location: action.payload,
    };
  }
  if (action.type === "UPDATE_OCCUPATION") {
    return {
      ...state,
      occupation: action.payload,
    };
  }
  if (action.type === "UPDATE_ATTRIBUTE") {
    return {
      ...state,
      attributes: action.payload,
    };
  }
  if (action.type === "UPDATE_CATEGORY_TAXONOMIES") {
    let updated_category_taxonomies = { ...state.category_taxonomies };
    let { req, res } = action.payload;
    if (!req) return state
    let { category } = req;
    updated_category_taxonomies[category] = res;
    return {
      ...state,
      category_taxonomies: updated_category_taxonomies,
    };
  }
  if (action.type === "UPDATE_PRODUCT_PREFS") {
    return {
      ...state,
      product_prefs: action.payload,
    };
  }
  if (action.type === "UPDATE_PRE_CATEGORY_DIMENSIONS") {
    return {
      ...state,
      pre_category_dimensions: action.payload,
    };
  }
  if (action.type === "UPDATE_LOADING") {
    return {
      ...state,
      loading: action.payload,
    };
  }
  if (action.type === "UPDATE_RANK_SCORES") {
    const updatedBrandScores = [...state.brand_scores];
    const { data, extras,baseResponses } = action.payload;
    if (!state.entity) return state;
    Array.isArray(data) &&
      data.forEach((item, index) => {
        let brandIndexItem = updatedBrandScores[index];
        if (brandIndexItem && brandIndexItem.favorability) {
          updatedBrandScores[index] = {
            name: item.name,
            rank: item.score,
            favorability: brandIndexItem.favorability,
            total: +item.score + +brandIndexItem.favorability,
            extras: {
              ...extras,
              ...brandIndexItem.extras,
              rank: [item],
            sentimentSources:brandIndexItem.sentimentSources,
            isValidated:brandIndexItem.isValidated
            },
          };
        } else {
          updatedBrandScores[index] = {
            ...updatedBrandScores[index],
            name: item.name,
            rank: item.score,
            extras: {
              ...extras,
              rank: [item],
            },
          };
        }
      });
    return {
      ...state,
      rank_scores: action.payload,
      brand_scores: updatedBrandScores,
    };
  }
  if (action.type === "REPLACE_BRAND_SCORES") {
    return {
      ...state,
      brand_scores: action.payload,
    };
  }
  if (action.type === "UPDATE_CUSTOM_TEMPLATE_ID") {
    return { ...state, customTemplateID: action.payload };
  }
  if (action.type === "UPDATE_CUSTOM_INDEX_SCORE_TYPE") {
    return { ...state, customIndexScoreType: action.payload };
  }
  if (action.type === "UPDATE_IS_BATCH_MODE") {
    return { ...state, isBatchMode: action.payload };
  }
  if (action.type === "UPDATE_IS_NON_BRANDED_QUESTIONS") {
    return { ...state, isNonBrandedQuestions: action.payload };
  }
  if (action.type === "UPDATE_IS_FACT_BASED_QUESTIONS") {
    return { ...state, isFactBasedQuestions: action.payload };
  }
  if (action.type === "UPDATE_RANK_COMPETITIVE_SET") {
    return { ...state, rankCompetitiveSet: action.payload };
  }
  if (action.type === "UPDATE_RANK_METHODOLOGY") {
    return { ...state, rankMethodology: action.payload };
  }
  if (action.type === "SET_METRIC_SETTING_OBJ") {
    return { ...state, metricSettingObject: action.payload ?? {} };
  }
  if (action.type === "UPDATE_KEYWORDS") {
    return { ...state, keywords: action.payload };
  }
  return state;
}

export default function DiscoveryProvider({ children }) {
  const { customerUiInfo } = useContext(AuthContext);
  const [state, dispatch] = useReducer(discoveryReducer, undefined, () => ({
    ...INITIAL_STATE,
    keywords: resolveDefaultKeywordOptions(customerUiInfo),
    languageList: getCachedLanguageList(),
  }));

  useEffect(() => {
    api.get("/utils/get-languages")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) {
          localStorage.setItem("languageList", JSON.stringify(res.data));
          dispatch({ type: "UPDATE_LANGUAGE_LIST", payload: res.data });
        }
      })
      .catch(() => {});
  }, []);

  function updateLoading(payload) {
    dispatch({ type: "UPDATE_LOADING", payload });
  }

  function updateId(payload) {
    dispatch({ type: "UPDATE_ID", payload });
  }

  function updateBrandScores(payload) {
    dispatch({ type: "UPDATE_BRAND_SCORE", payload });
  }

  function updateSources(payload) {
    dispatch({ type: "UPDATE_SOURCES", payload });
  }

  function updateCoreAttributes(payload) {
    dispatch({ type: "UPDATE_CORE_ATTRIBUTES", payload });
  }

  function updatedCompetitors(payload) {
    dispatch({ type: "UPDATE_COMPETITORS", payload });
  }

  function updateCategoryDimensions(payload) {
    dispatch({ type: "UPDATE_CATEGORY_DIMENSIONS", payload });
  }

  function updateNonBrandedQuestionsResults(payload) {
    dispatch({ type: "UPDATE_NON_BRANDED_QUESTIONS_RESULTS", payload });
  }

  function updateBrandDescription(payload) {
    dispatch({ type: "UPDATE_BRAND_DESCRIPTION", payload });
  }

  function updateBrandSnippets(payload) {
    dispatch({ type: "UPDATE_BRAND_SNIPPETS", payload });
  }

  function updatePositiveNegativeAttributes(payload) {
    dispatch({ type: "UPDATE_POSITVE_NEGATIVE_ATTRIBUTES", payload });
  }

  function updatedTopProducts(payload) {
    dispatch({ type: "UPDATE_TOP_PRODUCTS", payload });
  }

  function updateEntity(payload) {
    dispatch({ type: "UPDATE_ENTITY", payload });
  }

  function updatedLaunched(payload) {
    dispatch({ type: "UPDATE_LAUNCHED", payload });
  }

  function updateEntireState(payload) {
    dispatch({ type: "UPDATE_STATE", payload });
  }

  function updateTransactionId(payload) {
    dispatch({ type: "UPDATE_TRANSACTIONID", payload });
  }

  function updateStatus(payload) {
    dispatch({ type: "UPDATE_STATUS", payload });
  }

  function updateTransactionStatus(payload) {
    dispatch({ type: "UPDATE_TRANSACTIONSTATUS", payload });
  }

  function updateSourceValidationStatus(payload) {
    dispatch({ type: "UPDATE_SOURCEVALIDATIONSTATUS", payload });
  }

  function updateShareInfo(payload) {
    dispatch({ type: "UPDATE_SHARE_INFO", payload });
  }

  function updateTransactionHistory(payload) {
    dispatch({ type: "UPDATE_TRANSACTIONHISTORY", payload });
  }

  function resetState() {
    dispatch({ type: "RESET_STATE" });
  }

  function resetForRelaunch() {
    dispatch({ type: "RESET_FOR_RELAUNCH" });
  }

  function resetFormState(payload) {
    dispatch({ type: "RESET_FORMSTATE",payload });
  }

  function refreshState() {
    dispatch({ type: "REFRESH_STATE" });
  }

  function updatedBrandThemes(payload) {
    dispatch({ type: "UPDATE_BRAND_THEMES", payload });
  }

  function updateName(payload) {
    dispatch({ type: "UPDATE_NAME", payload });
  }

  function updateCategory(payload) {
    dispatch({ type: "UPDATE_CATEGORY", payload });
  }

  function updateCategoryType(payload) {
    dispatch({ type: "UPDATE_CATEGORY_TYPE", payload });
  }

  function updateMarket(payload) {
    dispatch({ type: "UPDATE_MARKET", payload });
  }

  function updateLLM(payload) {
    dispatch({ type: "UPDATE_LLM", payload });
  }

  function updateType(payload) {
    dispatch({ type: "UPDATE_TYPE", payload });
  }

  function updatedTaxonomies(payload) {
    dispatch({ type: "UPDATE_TAXONOMY", payload });
  }

  function updateLocation(payload) {
    dispatch({ type: "UPDATE_LOCATION", payload });
  }

  function updateOccupation(payload) {
    dispatch({ type: "UPDATE_OCCUPATION", payload });
  }

  function updateAttribute(payload) {
    dispatch({ type: "UPDATE_ATTRIBUTE", payload });
  }

  function updateRankScores(payload) {
    dispatch({ type: "UPDATE_RANK_SCORES", payload });
  }

  function updateAbortLaunch(payload) {
    dispatch({ type: "ABORT_LAUNCH", payload });

    setTimeout(() => {
      updateLoading(false);
      updatedLaunched(false);
      resetState();
    }, 100);
  }

  function replaceBrandScores(payload) {
    dispatch({ type: "REPLACE_BRAND_SCORES", payload });
  }

  function updateCustomTemplateID(payload) {
    dispatch({ type: "UPDATE_CUSTOM_TEMPLATE_ID", payload });
  }

  function updateCustomIndexScoreType(payload) {
    dispatch({ type: "UPDATE_CUSTOM_INDEX_SCORE_TYPE", payload });
  }

  function updateIsBatchMode(payload) {
    dispatch({ type: "UPDATE_IS_BATCH_MODE", payload });
  }

  function updateIsNonBrandedQuestions(payload) {
    dispatch({ type: "UPDATE_IS_NON_BRANDED_QUESTIONS", payload });
  }

  function updateIsFactBasedQuestions(payload) {
    dispatch({ type: "UPDATE_IS_FACT_BASED_QUESTIONS", payload });
  }

  function updateRankCompetitiveSet(payload) {
    dispatch({ type: "UPDATE_RANK_COMPETITIVE_SET", payload });
  }

  function updateRankMethodology(payload) {
    dispatch({ type: "UPDATE_RANK_METHODOLOGY", payload });
  }

  function setMetricSettingObj(payload) {
    dispatch({ type: "SET_METRIC_SETTING_OBJ", payload });
  }

  function updateKeywords(payload) {
    dispatch({ type: "UPDATE_KEYWORDS", payload });
  }

  function updateCategoryTaxonomies(payload) {
    dispatch({ type: "UPDATE_CATEGORY_TAXONOMIES", payload });
  }

  function updateProductPrefs(payload) {
    dispatch({ type: "UPDATE_PRODUCT_PREFS", payload });
  }

  function updatePreCategoryDimensions(payload) {
    dispatch({ type: "UPDATE_PRE_CATEGORY_DIMENSIONS", payload });
  }

  function updateLanguage(payload) {
    dispatch({ type: "UPDATE_LANGUAGE", payload });
  }

  // Generic recursive search to detect propensity RBI type anywhere in brand_scores
  function _findPropensity(obj) {
    if (!obj) return false;
    if (Array.isArray(obj)) return obj.some((item) => _findPropensity(item));
    if (typeof obj === "object") {
      if (obj?.rbiType === "propensityToBuy" || obj?.rbiType === "propensity_to_buy") return true;
      return Object.values(obj).some((v) => _findPropensity(v));
    }
    return false;
  }

  function isPropensityToBuy(opts = {}) {
    try {
      return _findPropensity(state.brand_scores);
    } catch (err) {
      return false;
    }
  }

  function mapMetricTitle(title) {
    if (!isPropensityToBuy()) return title;
    if (!title || typeof title !== "string") return title;
    return title.replace(/sentiment/gi, "Purchase Intent").replace(/visibility/gi, "Purchase Intent Visibility");
  }

  return (
    <DiscoveryContext.Provider
      value={{
        state: state,
        updateId,
        location: state.location,
        attributes: state.attributes,
        occupation: state.occupation,
        loading: state.loading,
        updateLoading: updateLoading,
        rank_scores: state.rank_scores,
        product_prefs: state.product_prefs,
        brand_scores: state.brand_scores,
        sources: state.sources,
        core_attributes: state.core_attributes,
        competitors: state.competitors,
        category_dimensions: state.category_dimensions,
        non_branded_questions_results: state.non_branded_questions_results,
        brand_description: state.brand_description,
        brand_snippets: state.brand_snippets,
        brand_themes: state.brand_themes,
        positive_negative_attributes: state.positive_negative_attributes,
        top_products: state.top_products,
        entity: state.entity,
        name: state.name,
        category: state.category,
        categoryType: state.categoryType,
        market: state.market,
        llm: state.llm,
        is_launched: state.is_launched,
        taxonomies: state.taxonomies,
        type: state.type,
        pre_category_dimensions: state.pre_category_dimensions,
        abort_launch: state.abort_launch,
        category_taxonomies: state.category_taxonomies,
        language: window.selectedLanguage || state.language,
        languageList: state.languageList,
        is_resetFormState: state.is_resetFormState,
        updateBrandScores,
        updateSources,
        updateCoreAttributes,
        updatedCompetitors,
        updateCategoryDimensions,
        updateNonBrandedQuestionsResults,
        updateBrandDescription,
        updateBrandSnippets,
        updatePositiveNegativeAttributes,
        updatedTopProducts,
        updateEntity,
        updateName,
        updatedLaunched,
        updateEntireState,
        resetState,
        resetForRelaunch,
        resetFormState,
        refreshState,
        updatedBrandThemes,
        updateCategory,
        updateCategoryType,
        updateMarket,
        updateLLM,
        updateType,
        updatedTaxonomies,
        updateAbortLaunch,
        updateAttribute,
        updateLocation,
        updateOccupation,
        updateCategoryTaxonomies,
        updateProductPrefs,
        updatePreCategoryDimensions,
        updateRankScores,
        replaceBrandScores,
        updateLanguage,
        updateTransactionId,
        updateStatus,
        updateTransactionStatus,
        updateSourceValidationStatus,
        updateShareInfo,
        updateTransactionHistory,
        updateCustomTemplateID,
        updateCustomIndexScoreType,
        updateRankCompetitiveSet,
        updateRankMethodology,
        updateIsBatchMode,
        updateIsNonBrandedQuestions,
        updateIsFactBasedQuestions,
        setMetricSettingObj,
        updateKeywords,
        customTemplateID: state.customTemplateID,
        customIndexScoreType: state.customIndexScoreType,
        isBatchMode: state.isBatchMode,
        isNonBrandedQuestions: state.isNonBrandedQuestions,
        isFactBasedQuestions: state.isFactBasedQuestions,
        rankCompetitiveSet: state.rankCompetitiveSet,
        rankMethodology: state.rankMethodology,
        metricSettingObject: state.metricSettingObject,
        keywords: state.keywords,
      }}
    >
      {children}
    </DiscoveryContext.Provider>
  );
}
